'use client';

import { addToFolderEventAction, incrementTelemetry } from 'core-events';
import { buildBaseInteractionMeta } from 'core-events/src/shared/BaseEventMeta.client';
import { usePathname, useSearchParams } from 'next/navigation';
import { JSX, useContext, useEffect, useMemo, useState } from 'react';
import { DetailsStockItem, StockItem } from 'search-gateway-client';
import { Folder, UpdateFolderResponse } from 'stockblocks-client';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@videoblocks/design-system';
import { OriginArea } from '@videoblocks/events-ts/lib/storyblocks/content/OriginAreaEnum';
import { type SearchIdentifiers } from '@videoblocks/events-ts/lib/storyblocks/content/StockItemAddToFolderEvent';
import { FolderCheck, FolderPlus } from '@videoblocks/react-icons';

import Popover from '../../../__temp__design_system/atoms/Popover/Popover';
import { revalidateFolders } from '../../../actions/revalidate';
import IconButton from '../../atoms/IconButton';
import StockItemCardContext from '../../cards/AudioStockItemCard/context/StockItemCardContext';
import FolderSelector from '../FolderSelector/FolderSelector';
import SignUpForFeaturePopover from '../SignUpForFeaturePopover';
import { FolderGroup, StockItemContext } from '../StockItemTypes';

export type AddToFolderButtonProps = {
    isLoggedIn?: boolean;
    rootHovered: boolean;
    folderGroup: FolderGroup;
    className?: string;
    hasText?: boolean;
    iconOnLeft?: boolean;
    stockItem: StockItem | DetailsStockItem;

    // Optional (if you have them handy from search flow)
    searchIdentifiers?: SearchIdentifiers | null;
    originArea?: OriginArea | null; // e.g. OriginArea.SEARCH_RESULTS
};

const AddToFolderButton = (props: AddToFolderButtonProps): JSX.Element => {
    const {
        isLoggedIn = false,
        className = '',
        rootHovered,
        folderGroup,
        hasText,
        iconOnLeft = true,
        stockItem,
        originArea = null,
        searchIdentifiers = null,
    } = props;
    const { stockItemContext: context } = useContext(StockItemCardContext);
    const [showTooltip, setShowTooltip] = useState(false);
    const [folders, setFolders] = useState<Record<string, Folder>>(props.folderGroup.folders || {});

    const pathname = usePathname();
    const searchParams = useSearchParams();
    const params = searchParams?.toString();
    const currentUrl = `${pathname}${params ? `?${params}` : ''}`;

    // i.e. AudioStockItemCard.isInRecommendedMusicDrawer
    // todo: consider drawerType === RECOMMENDED_MUSIC
    const telemetryKey = context === StockItemContext.DRAWER ? 'recommended_music' : 'search';

    const isStockItemInAnyFolder = useMemo(
        () => Object.values(folders ?? {}).some((folder) => folder.contentIds.includes(stockItem.id)),
        [folders, stockItem.id],
    );

    const tooltipText = isStockItemInAnyFolder ? 'Added to Folder' : 'Add to Folder';

    const onClick = () => {
        void incrementTelemetry(`favorite.clicked.${telemetryKey}`);
    };

    const onHover = () => {
        // TODO: Find correct telemetry tag
        void incrementTelemetry('');
        setShowTooltip(true);
    };

    const onMouseLeave = () => {
        setShowTooltip(false);
    };

    const onBlur = () => {
        setShowTooltip(false);
    };

    const refreshFolders = async (revalidate = false) => {
        if (revalidate) await revalidateFolders();
        const newFolders = await folderGroup.getAllFolders();
        setFolders(newFolders?.bins ?? {});
    };

    useEffect(() => {
        // refresh folders if user has hovered over the audio card (to get latest folder state)
        if (!rootHovered) return;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        void refreshFolders();
    }, [rootHovered]);

    const buildButtonText = (text: string, iconOnLeft: boolean) => {
        return <span className={iconOnLeft ? 'ml-1' : 'mr-2'}>{text}</span>;
    };

    const getFolderBtn = () => {
        const icon = isStockItemInAnyFolder ? (
            <FolderCheck className="w-6 h-6 fill-current [&>path:last-child]:fill-green-600" />
        ) : (
            <FolderPlus className="fill-current w-6 h-6" />
        );

        return (
            <IconButton
                onMouseEnter={onHover}
                onMouseLeave={onMouseLeave}
                onFocus={onHover}
                onKeyDown={onClick}
                onBlur={onBlur}
                onClick={onClick}
                className={twMerge(className, hasText && 'w-fit')}
                aria-label={tooltipText}
                title={tooltipText}
                as="button"
            >
                {hasText ? (
                    <div className="flex flex-nowrap items-center">
                        {!iconOnLeft && buildButtonText(tooltipText, iconOnLeft)}
                        {icon}
                        {iconOnLeft && buildButtonText(tooltipText, iconOnLeft)}
                    </div>
                ) : (
                    icon
                )}
            </IconButton>
        );
    };

    const trigger = isLoggedIn ? (
        <Popover trigger={getFolderBtn()} className="rounded-xl">
            <FolderSelector
                bins={folders}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                folderClickedCallback={async ({ bin, event }) => {
                    const shouldRemove = bin.contentIds.includes(stockItem.id);

                    let updatedFolder: UpdateFolderResponse;
                    if (shouldRemove) {
                        updatedFolder = await folderGroup.removeStockItemsFromFolder(bin.uniqueId, [stockItem.id]);
                        void incrementTelemetry(`favorite.removed.${telemetryKey}`);
                    } else {
                        updatedFolder = await folderGroup.addStockItemsToFolder(bin.uniqueId, [stockItem.id]);
                        void incrementTelemetry(`favorite.added.${telemetryKey}`);

                        try {
                            const { visitorCookieId } = buildBaseInteractionMeta(); // httpOnly session handled server-side

                            void addToFolderEventAction({
                                visitorCookieId,
                                userFolderId: bin.id,
                                stockItem,
                                originArea,
                                searchIdentifiers,
                                relatedEventIds: null,
                            });
                        } catch (e) {
                            console.error('AddToFolder event emit failed:', e);
                        }
                    }

                    setFolders({
                        ...folders,
                        ...updatedFolder.bins,
                    });

                    // onClickAction(event);
                    setShowTooltip(false);
                }}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                createNewClickedCallback={async ({ name }) => {
                    await folderGroup.createFolder(name, [stockItem.id]);
                    await refreshFolders(true);
                }}
                isLoading={false}
            />
        </Popover>
    ) : (
        <SignUpForFeaturePopover
            postSignUpUrl={`/api/member/folders/favorite-then-visit/${stockItem.id}`}
            tagLine="Start organizing assets for your creative projects!"
            trigger={getFolderBtn()}
            className="mr-2"
            loginRedirect={`/login?redirect=${currentUrl}`}
        />
    );

    return <Tooltip open={showTooltip} title={hasText ? '' : tooltipText} trigger={trigger} />;
};

export default AddToFolderButton;
